import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fontsource-variable/inter';
import '@fontsource/ibm-plex-sans-arabic/400.css';
import '@fontsource/ibm-plex-sans-arabic/500.css';
import '@fontsource/ibm-plex-sans-arabic/600.css';
import '@fontsource/ibm-plex-sans-arabic/700.css';
import './index.css';
import './i18n'; // Initialize i18n
import App from './App';
import { Provider } from 'react-redux';
import { store } from './store';
import * as Sentry from '@sentry/react';
import axios from 'axios';
import { applyRequestHeaders } from './utils/requestHeaders';
import ErrorBoundary from './components/ErrorBoundary';
import * as chatwoot from './services/chatwoot';
import { ThemeProvider } from './context/ThemeContext';
import { installChunkErrorAutoReload } from './utils/chunkReload';
import { installLatinDigitInput } from './utils/latinDigits';
import toast from 'react-hot-toast';
import i18n from 'i18next';
import { planLimitMessage } from './utils/planFeatures';
import { localizePermissionDenied } from './utils/apiError';
import {
  applyActiveBranch,
  effectiveBranchId,
  fetchAuthorizedBranches,
} from './utils/authorizedBranches';

// Catch post-deploy chunk-load failures that escape the React tree (a stray
// import() rejection) and reload once to pick up the new build.
installChunkErrorAutoReload();

// Configure axios
// Use empty baseURL to let Nginx proxy handle /api requests
axios.defaults.baseURL = '';

// Same headers the invoice-scan stream's fetch sends. The body lives in
// utils/requestHeaders (applyRequestHeaders) so it can be unit-tested.
axios.interceptors.request.use((config) => applyRequestHeaders(config));

// Kept in lock-step with backend/utils/subscriptionState.js.
const SUBSCRIPTION_BLOCK_CODES = new Set([
  'SUBSCRIPTION_EXPIRED',
  'SUBSCRIPTION_SUSPENDED',
  'COMPANY_DEACTIVATED',
  // The gate could not read the company's status columns and refuses to guess.
  // Rare and always a backend bug, but it must land on a real screen rather
  // than a 402 nothing handles.
  'SUBSCRIPTION_STATUS_UNAVAILABLE',
]);

// Session-expiry handling: when an authenticated request comes back 401 (the
// token expired or was invalidated), clear the stale session and bounce to the
// login page once. Without this, every call silently fails and the user appears
// "stuck" (e.g. can't create a job card). Never redirect for the login/OTP calls
// themselves, and never redirect anonymous (no-token) public-page traffic.

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isAuthCall = url.includes('/api/auth/');
    const hadSession = !!localStorage.getItem('token');
    // Plan limits (user/branch ceiling reached, AI quota spent, a feature not
    // in the plan) are surfaced HERE rather than in each screen's catch, so
    // there is no path where the button just does nothing. The server sends a
    // typed code and the raw numbers; the sentence is built here so it follows
    // the user's language, not the request's.
    const planLimit = planLimitMessage(error);
    if (planLimit && !error?.config?.silentPlanLimit) {
      toast.error(i18n.t(planLimit.key, planLimit.params), { id: planLimit.key });
    }

    // A refused permission arrives as English built from catalog keys ("You do
    // not have permission to read reports"). Screens that print
    // `response.data.error` directly would show exactly that to an Arabic user,
    // so the field is put into the user's language here, once, for all of them.
    // No toast (the calling screen already shows the message) and no early
    // return (the single reject at the bottom is the only one). Guarded: a
    // throw here would replace the original error for every caller.
    try {
      localizePermissionDenied((key, values) => i18n.t(key, values), error);
    } catch {
      // Leave the server's English in place.
    }

    // 402 = this TENANT is locked out (deactivated / suspended / subscription
    // term spent, including its grace days) — not this session. Deliberately a
    // different status from 401 so we do NOT clear the token and bounce to
    // /login: the user would just log in again (login 402s too) and be told
    // nothing. Send them to the wall once, with the reason.
    const code = error?.response?.data?.code;
    if (status === 402 && SUBSCRIPTION_BLOCK_CODES.has(code)) {
      if (window.location.pathname !== '/subscription-ended') {
        const endsAt = error?.response?.data?.subscription?.endsAt || '';
        window.location.assign(
          `/subscription-ended?code=${encodeURIComponent(code)}` +
            (endsAt ? `&endsAt=${encodeURIComponent(endsAt)}` : '')
        );
      }
      // No early return: the single reject at the bottom is the only one, and
      // supportChatScope.test.js reads this handler's source up to it.
    }

    // Per-branch roles: a user whose role became different per branch while in
    // All mode is refused "All branches" (403 BRANCH_SCOPE_ALL_MIXED_ROLES).
    // Move them to one branch (the last real one, else home, else the first
    // they may use) and reload; the reloaded /auth/me then hides the choice.
    // Only while the stored value is 'all', so the reload cannot loop.
    if (
      status === 403 &&
      code === 'BRANCH_SCOPE_ALL_MIXED_ROLES' &&
      localStorage.getItem('activeGarageId') === 'all'
    ) {
      const toBranch = (id) => {
        if (!id || localStorage.getItem('activeGarageId') !== 'all') return;
        applyActiveBranch(id);
        window.location.reload();
      };
      const known = effectiveBranchId() || store.getState().auth.user?.garage_id;
      if (known) {
        toBranch(known);
      } else {
        fetchAuthorizedBranches({ singleBranch: true }).then(
          (list) => toBranch((list.find((b) => b.is_home) || list[0])?.id),
          () => {}
        );
      }
    }

    if (status === 401 && hadSession && !isAuthCall) {
      // Chatwoot keeps its own session on its own origin; only $chatwoot.reset()
      // clears it, and reloading this page does not. Expiry is the COMMON way a
      // session ends on a shared workshop terminal — far more common than the
      // logout button, which is the only other caller of reset() — so without
      // this the next person to sign in on the same browser inherits the
      // previous user's support conversation.
      chatwoot.reset();
      localStorage.removeItem('token');
      localStorage.removeItem('activeGarageId');
      // P8: an expired session is not a login — never offer the branch pick.
      sessionStorage.removeItem('mm.pickBranchOnLogin');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.assign('/login?expired=1');
      }
    }
    return Promise.reject(error);
  }
);

// Initialize Sentry
if (process.env.REACT_APP_SENTRY_DSN) {
  Sentry.init({
    dsn: process.env.REACT_APP_SENTRY_DSN,
    environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || 'development',
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Register the minimal service worker so the app is installable from Chrome
// (PWA). Production builds only; the SW does no caching (see
// public/service-worker.js), so it can't cause stale-bundle issues.
if ('serviceWorker' in navigator && process.env.NODE_ENV === 'production') {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/service-worker.js').catch(() => {});
  });
}

// Normalise Arabic and Persian digits to Latin digits across all input and textarea fields
installLatinDigitInput();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <Provider store={store}>
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </Provider>
    </ErrorBoundary>
  </React.StrictMode>
);
